import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Table } from '@components/Common';
import ContentLoader, { ContentContainer } from '@components/Common/Loaders/ContentLoader';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import { HeadingRow, Layout, MainContent, SideBar } from '@components/Common/SideBarLayout';
import TankBalance from '@components/Common/TankBalance';
import TankEditor from '@components/Pages/Tanks/TankEditor';

// TanStack Query hooks
import {
  useTanksQuery,
  useTankBalancesQuery,
  useTankSurfaceLevelsQuery,
  combineTanksWithBalances,
  Tank,
  FlowMeterType
} from '@api/queries/tanks';

// Re-export types for other components
export type { Tank } from '@api/queries/tanks';

const TanksList: React.FC = () => {
  const { t } = useTranslation();

  // Local UI state for editor
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedTank, setSelectedTank] = useState<Tank | null>(null);

  // TanStack Query for tanks data
  const { data: tanks = [], isLoading: isLoadingTanks } = useTanksQuery();
  const { data: balances = [], isLoading: isLoadingBalances } = useTankBalancesQuery();
  const { data: surfaceLevels = [], isLoading: isLoadingSurfaceLevels } = useTankSurfaceLevelsQuery();

  const isLoading = isLoadingTanks || isLoadingBalances || isLoadingSurfaceLevels;

  // Combine tanks with their balances and surface levels
  const tanksWithBalances = combineTanksWithBalances(tanks, balances, surfaceLevels);
  const enabledTanks = tanksWithBalances.filter(i => i.isEnabled);

  const openEditor = (tank: Tank | null) => {
    setSelectedTank(tank);
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setSelectedTank(null);
  };

  const formKey = (selectedTank && selectedTank.id) || 'new';

  return (
    <>
      <HeadingRow>
        <Button primary={true} onClick={() => openEditor(null)}>
          {t('add_new')}
        </Button>
        {isLoading && <OperationLoader title={t('updating') + '...'} />}
      </HeadingRow>
      <Layout>
        <MainContent>
          <ContentContainer>
            {isLoading && tanks.length === 0 && <ContentLoader />}
            <Table
              data={enabledTanks}
              columns={[
                {
                  Header: 'Id',
                  id: 'id',
                  accessor: 'id',
                  show: false
                },
                {
                  Header: t('tank.name'),
                  id: 'name',
                  maxWidth: 150,
                  accessor: 'name'
                },
                {
                  Header: () => (
                    <span>
                      {t('tank.capacity')}{' '}
                      <sup>
                        m<sup>3</sup>
                      </sup>
                    </span>
                  ),
                  id: 'capacity',
                  maxWidth: 120,
                  accessor: (tank: Tank) => tank.capacity.toLocaleString('fi')
                },
                {
                  Header: () => (
                    <span>
                      {t('tank.bookkeeping_balance')} <sup>t</sup>
                    </span>
                  ),
                  id: 'balance',
                  width: 220,
                  filterable: false,
                  sortable: false,
                  Cell: row => <TankBalance tankBalance={row.original.balance} textWidth={80} />
                },
                {
                  Header: () => (
                    <span>
                      {t('tank.measured_balance')}{' '}
                      <sup>
                        m<sup>3</sup>
                      </sup>
                    </span>
                  ),
                  id: 'surfaceLevel',
                  width: 220,
                  filterable: false,
                  sortable: false,
                  Cell: row => <TankBalance tankBalance={row.original.surfaceLevel} textWidth={80} />
                },
                {
                  Header: t('tank.product'),
                  id: 'productName',
                  maxWidth: 250,
                  accessor: 'productName'
                },
                {
                  Header: t('tank.customer'),
                  id: 'customerName',
                  maxWidth: 200,
                  accessor: (tank: Tank) => (tank.customers ? tank.customers.map(i => i.customerName).join(', ') : '')
                },
                {
                  Header: t('tank.terminal'),
                  id: 'terminalName',
                  maxWidth: 170,
                  accessor: 'terminalName'
                },
                {
                  Header: 'customerId',
                  id: 'customerId',
                  maxWidth: 170,
                  accessor: 'customerId',
                  show: false
                },
                {
                  Header: 'terminalId',
                  id: 'terminalId',
                  maxWidth: 170,
                  accessor: 'terminalId',
                  show: false
                },
                {
                  Header: 'productId',
                  id: 'productId',
                  maxWidth: 170,
                  accessor: 'productId',
                  show: false
                },
                {
                  Header: t('tank.flow_meter_type'),
                  id: 'flowMeterType',
                  accessor: 'flowMeterType',
                  show: true,
                  maxWidth: 100,
                  Cell: ({ value }) => {
                    return t(`tank.flow_meter_types.${FlowMeterType[value].toLowerCase()}`);
                  },
                  filterMethod: (filter, row) => (filter.value ? row[filter.id] === Number(filter.value) : true),
                  Filter: ({ filter, onChange }) => (
                    <select
                      onChange={event => onChange(event.target.value)}
                      style={{ width: '100%' }}
                      value={filter ? filter.value : ''}
                    >
                      <option value="">{t('all')}</option>
                      <option value={FlowMeterType.None}>{t('tank.flow_meter_types.none')}</option>
                      <option value={FlowMeterType.Normal}>{t('tank.flow_meter_types.normal')}</option>
                      <option value={FlowMeterType.HydrogenPeroxide}>
                        {t('tank.flow_meter_types.hydrogenperoxide')}
                      </option>
                    </select>
                  )
                },
                {
                  Header: t('tank.notes'),
                  id: 'notes',
                  accessor: 'notes'
                }
              ]}
              defaultPageSize={50}
              defaultSortColumn="name"
              onRowSelected={(rowInfo: any) => openEditor(rowInfo.original)}
              rowSelector={i => isEditorOpen && selectedTank && selectedTank.id === i.original.id}
            />
          </ContentContainer>
        </MainContent>
        {isEditorOpen && (
          <SideBar>
            <TankEditor
              key={formKey}
              tankToEdit={selectedTank}
              onClose={closeEditor}
            />
          </SideBar>
        )}
      </Layout>
    </>
  );
};

export default TanksList;
